import { EventEmitter } from '../events/EventEmitter';
class ScreenLocker extends EventEmitter<{
  forcedStateChange: {
    hasReleasedFirstTime: boolean;
    isForced: boolean;
  };
  lockStateChange: {
    hasReleasedFirstTime: boolean;
    isLocked: boolean;
    lockName: string;
  };
  releaseForFirstTime: null;
  ready: null;
}> {
  #hasReleasedFirstTime = false;

  #wasReleasedFirstTime = false;

  #isForced = false;

  #locks: Record<string, boolean> = {
    common: false,
  };

  get hasReleasedFirstTime() {
    return this.#hasReleasedFirstTime;
  }

  get isForced() {
    return this.#isForced;
  }

  /**
   * Permite saber si un bloqueo determinado está activo o si la clase tiene
   * forceLock activo.
   */
  isLocked(lockName = 'common') {
    return this.#locks[lockName] || this.#isForced;
  }

  constructor() {
    super();

    this.emit('ready', null);
  }

  #shoutLockState(lockName?: string) {
    if (this.#isForced || lockName === undefined) {
      this.emit('forcedStateChange', {
        isForced: this.#isForced,
        hasReleasedFirstTime: this.#hasReleasedFirstTime,
      });
    } else {
      if (
        lockName === 'common' &&
        !this.#wasReleasedFirstTime &&
        this.#hasReleasedFirstTime
      ) {
        this.emit('releaseForFirstTime', null);
      }

      this.emit('lockStateChange', {
        lockName,
        hasReleasedFirstTime: this.#hasReleasedFirstTime,
        isLocked: this.#locks[lockName],
      });
    }
  }

  lock(lockName = 'common') {
    this.#locks[lockName] = true;
    this.#shoutLockState(lockName);
  }

  unlock(lockName = 'common') {
    if (lockName === 'common') this.#hasReleasedFirstTime = true;
    this.#locks[lockName] = false;
    this.#shoutLockState(lockName);
  }

  force() {
    this.#isForced = true;
    this.#shoutLockState();
  }

  releaseForced() {
    this.#isForced = false;
    this.#shoutLockState();
  }
}

/**
 * Esta clase no implementa ninguna funcionalidad de bloqueo, sino solamente se
 * encarga de la lógica y de los eventos.
 *
 * El concepto es que hay n niveles de bloqueo y además existe el bloqueo
 * forzado. Cada uno de los niveles recibe un nombre, por defecto se realizan
 * operaciones contra el bloque 'common' si no se pasa ninguno, pero
 * podría utilizarse cualquier otro. Esto es así con el fin de implementar
 * varios bloqueos en la misma clase, ejemplos: el bloqueo común, el bloqueo
 * lineal de las tablas, bloque con pantalla blanca en el inicio de formularios,
 * o cualquier otro que se desee. Para los bloqueos estándar se utilizan los
 * métodos lock y unlock.
 *
 * El bloqueo forzado por otra parte es un estado general que se puede alterar
 * mediante los métodos force y releaseForced. Cada vez que se bloquee o
 * desbloquee la pantalla, se lanzará un evento lockStateChange sin lockName y
 * con forced = true.
 *
 * Además, provee un evento para saber cuándo se desbloquea la pantalla
 * efectivamente por primera vez para cada tipo de bloqueo. Es posible a su vez
 * resetear la clase para que este evento sea lanzado nuevamente en el próximo
 * desbloqueo mediante resetRefreshFirstTime.
 *
 * El evento releasForFirstTime se dispara únicamente para el bloqueo 'common'.
 * Esto es así ya que sino podría dar lugar a confusión en situaciones donde no
 * se controle el nombre del bloqueo.
 */
const screenLocker = new ScreenLocker();

export { screenLocker };
